import api, {
  getStoredAccessToken,
  API_BASE_URL,
  extractApiError,
  unwrapSingle,
  performTokenRefresh,
  clearSession,
} from "@/lib/api";

let wsInstance = null;
let wsConnectTimer = null;
let wsKeepaliveTimer = null;
const wsSubscribers = new Set();       // incident messages
const wsVehicleSubscribers = new Set(); // vehicle_state messages

/**
 * Get a single incident with full details
 */
export async function getIncident(incidentId) {
  try {
    const response = await api.get(`/incidents/${incidentId}`);
    return unwrapSingle(response);
  } catch (err) {
    console.error(`[incidentService] getIncident(${incidentId}) failed:`, err);
    throw err;
  }
}

/**
 * Acknowledge an incident (update status to ACKNOWLEDGED)
 */
export async function acknowledgeIncident(incidentId, description = "") {
  try {
    const payload = { status: "ACKNOWLEDGED" };
    if (description?.trim()) {
      payload.note = description.trim();
    }
    const { data } = await api.post(`/incidents/${incidentId}/service-response`, payload);
    return data;
  } catch (err) {
    console.error(`[incidentService] acknowledgeIncident(${incidentId}) failed:`, err);
    throw err;
  }
}

/**
 * Extract the error message from API response
 */
function extractErrorMessage(err) {
  return extractApiError(err).message || "An error occurred";
}

/**
 * Build WebSocket URL with token
 */
function buildWsUrl(token) {
  const base = API_BASE_URL.replace(/^http/, "ws").replace(/\/$/, "");
  return `${base}/ws/connect?token=${encodeURIComponent(token)}`;
}

/**
 * Connect to the incident WebSocket and listen for incident messages
 */
export function connectIncidentSocket() {
  if (wsInstance && (wsInstance.readyState === WebSocket.OPEN || wsInstance.readyState === WebSocket.CONNECTING)) {
    console.log("[incidentService] WebSocket already connected or connecting");
    return;
  }

  let isDisposed = false;

  const connect = () => {
    if (isDisposed) return;
    if (wsInstance && (wsInstance.readyState === WebSocket.OPEN || wsInstance.readyState === WebSocket.CONNECTING)) {
      return;
    }

    // Always read the fresh token — it rotates every 15 min via /auth/refresh.
    // Capturing it in the outer scope causes every reconnect to use a stale token.
    const token = getStoredAccessToken();
    if (!token) {
      console.warn("[incidentService] No access token available for WebSocket connection");
      return;
    }

    try {
      const wsUrl = buildWsUrl(token);
      wsInstance = new WebSocket(wsUrl);

      wsInstance.onopen = () => {
        if (isDisposed) {
          wsInstance.close(1000, "Disposed");
          return;
        }
        console.log("[incidentService] WebSocket connected");

        // Send keepalive ping every 20 seconds
        if (wsKeepaliveTimer) clearInterval(wsKeepaliveTimer);
        wsKeepaliveTimer = setInterval(() => {
          if (wsInstance && wsInstance.readyState === WebSocket.OPEN) {
            try {
              wsInstance.send(JSON.stringify({ type: "ping" }));
            } catch (e) {
              // ignore send errors
            }
          }
        }, 20000);
      };

      wsInstance.onmessage = (evt) => {
        let parsed;
        try {
          parsed = JSON.parse(evt.data);
        } catch {
          return;
        }

        if (parsed?.type === "incident" && parsed?.data) {
          wsSubscribers.forEach((callback) => {
            try { callback(parsed.data); } catch (e) {
              console.error("[incidentService] Subscriber callback error:", e);
            }
          });
        }

        if (parsed?.type === "vehicle_state" && parsed?.data) {
          wsVehicleSubscribers.forEach((callback) => {
            try { callback(parsed.data); } catch (e) {
              console.error("[incidentService] Vehicle subscriber callback error:", e);
            }
          });
        }
      };

      wsInstance.onerror = (err) => {
        console.error("[incidentService] WebSocket error:", err);
      };

      wsInstance.onclose = async (evt) => {
        if (isDisposed) return;

        if (wsKeepaliveTimer) clearInterval(wsKeepaliveTimer);
        wsKeepaliveTimer = null;

        if (evt?.code === 4001) {
          console.warn("[incidentService] WebSocket closed with 4001 (permissions/session changed). Attempting token refresh before reconnect");
          try {
            await performTokenRefresh();
            if (!isDisposed) {
              connect();
            }
          } catch (refreshErr) {
            console.error("[incidentService] Token refresh failed after WebSocket 4001 close:", refreshErr);
            clearSession();
            window.location.href = "/login";
          }
          return;
        }

        console.log("[incidentService] WebSocket closed, reconnecting in 3s");
        wsConnectTimer = setTimeout(connect, 3000);
      };
    } catch (err) {
      console.error("[incidentService] WebSocket connection failed:", err);
      if (!isDisposed) {
        wsConnectTimer = setTimeout(connect, 3000);
      }
    }
  };

  connect();

  // Return a cleanup function
  return () => {
    isDisposed = true;
    if (wsConnectTimer) clearTimeout(wsConnectTimer);
    if (wsKeepaliveTimer) clearInterval(wsKeepaliveTimer);
    if (wsInstance) {
      wsInstance.onopen = null;
      wsInstance.onmessage = null;
      wsInstance.onerror = null;
      wsInstance.onclose = null;
      if (wsInstance.readyState === WebSocket.OPEN) {
        wsInstance.close(1000, "Cleanup");
      }
      wsInstance = null;
    }
  };
}

/**
 * Disconnect from the incident WebSocket
 */
export function disconnectIncidentSocket() {
  if (wsConnectTimer) clearTimeout(wsConnectTimer);
  if (wsKeepaliveTimer) clearInterval(wsKeepaliveTimer);
  if (wsInstance) {
    wsInstance.onopen = null;
    wsInstance.onmessage = null;
    wsInstance.onerror = null;
    wsInstance.onclose = null;
    if (wsInstance.readyState === WebSocket.OPEN) {
      wsInstance.close(1000, "Disconnect");
    }
    wsInstance = null;
  }
  wsSubscribers.clear();
  wsVehicleSubscribers.clear();
}

/**
 * Subscribe to incident WebSocket messages
 * Returns an unsubscribe function
 */
export function subscribeToIncidents(callback) {
  if (typeof callback !== "function") {
    throw new Error("Callback must be a function");
  }
  wsSubscribers.add(callback);
  return () => {
    wsSubscribers.delete(callback);
  };
}

/**
 * Unsubscribe from incident WebSocket messages
 */
export function unsubscribeFromIncidents(callback) {
  wsSubscribers.delete(callback);
}

/**
 * Subscribe to vehicle_state WebSocket messages.
 * Returns an unsubscribe function.
 */
export function subscribeToVehicleState(callback) {
  if (typeof callback !== "function") {
    throw new Error("Callback must be a function");
  }
  wsVehicleSubscribers.add(callback);
  return () => {
    wsVehicleSubscribers.delete(callback);
  };
}

/**
 * Unsubscribe from vehicle_state WebSocket messages
 */
export function unsubscribeFromVehicleState(callback) {
  wsVehicleSubscribers.delete(callback);
}
